/* src/views/Restaurant/ClientManagement/ReservationManagement/components/
   ReservationConfiguration/ReservationConfiguration.tsx */
   import React, { useCallback } from 'react'
   import { Dropdown,OverlayTrigger,Tooltip } from 'react-bootstrap'
   import { FaInfoCircle, FaLightbulb } from 'react-icons/fa'
   import {
     patchReservationConfiguration,
     PatchUpdate,
     updateRequiredApproval,
     updateConfirmationRequired,
     updateConfirmationPeriod,
     updateReservationPeriod,
   } from '../../../../../../crud/management_api/reservations_configuration'
   import { toast } from 'react-toastify'
   import { ReservationConfiguration, ReservationResponse } from '../../types'
   import ReservationDays from './components/ReservationDays/ReservationDays'
   import './ReservationConfiguration.scss'
   
   interface Props {
     data: ReservationConfiguration
     setData: React.Dispatch<React.SetStateAction<ReservationConfiguration>>
   }
   
   const ReservationConfigurationPage: React.FC<Props> = ({ data, setData }) => {
     const hours = Array.from({ length: 72 }, (_, i) => i + 1)
   
     // normalize API response → our RC type
     const toRC = (rp: ReservationResponse): ReservationConfiguration => ({
       local_id:      rp.local_id,
       configuration: rp.object,
       last_modified: rp.timestamp,
     })
   
     // generic PATCH dispatcher
     const applyPatch = useCallback(
       async (updates: PatchUpdate[]) => {
         try {
           const res = await patchReservationConfiguration(data.local_id, updates)
           setData(toRC((res as any).data))
           toast.success('Configuración guardada')
         } catch (err) {
           console.error(err)
           toast.error('No se pudo guardar la configuración')
         }
       },
       [data.local_id, setData]
     )
   
     // single-field toggles
     const makeToggle = (
       field: 'required_approval' | 'confirmation_required',
       fn: (id: number, val: boolean) => Promise<any>
     ) => async () => {
       try {
         const res = await fn(data.local_id, !data.configuration[field])
         setData(toRC(res.data))
         toast.success('Actualizado')
       } catch {
         toast.error('No se pudo actualizar')
       }
     }
   
     // single-field dropdowns
     const handlePeriod = async (
       field: 'confirmation_period' | 'reservation_period',
       value: number | null,
       fn: (id: number, val: number | null) => Promise<any>
     ) => {
       try {
         const res = await fn(data.local_id, value)
         setData(toRC(res.data))
         toast.success('Actualizado')
       } catch {
         toast.error('No se pudo actualizar')
       }
     }
   
   return (
     <div className="reservation-config-container">
       <h3>Configuración de Reservas</h3>
       <p className="config-description">
         Personaliza cómo tu restaurante gestiona las reservas. Define reglas de aprobación, períodos de confirmación y disponibilidad.
       </p>

       <div className="info-banner">
         <FaLightbulb className="info-icon" />
         <div className="info-content">
           <div className="info-title">Guía rápida</div>
           <p className="info-text">
             Configura las políticas de reserva según tus necesidades. Puedes requerir aprobación manual, 
             establecer períodos de confirmación y definir con cuánta anticipación pueden reservar tus clientes.
           </p>
         </div>
       </div>

       <div className="config-form">
         <div className="form-section-title">Políticas de Aprobación</div>           {/* Required approval */}
           <div className="form-row">
             <div className="label-with-icon">
               <OverlayTrigger
                  placement="top" // Posición del tooltip
                  overlay={
                    <Tooltip id="tooltip-item-name">
                      <div style={{ textAlign: 'left', whiteSpace: 'normal' }}>
                        El local gastronómico puede aprobar, rechazar o cancelar una reserva. Si esta opción está desactivada las reservas serán automáticamente confirmadas
                      </div>
                    </Tooltip>
                  }
                >
                  <span className="ms-2 info-create-item" style={{ cursor: 'pointer' }}>
                    <FaInfoCircle size={15} color="#6c757d" /> {/* Ícono de ayuda */}
                  </span>
                </OverlayTrigger>
               <span>Requiere aprobación del local gastronómico</span>
             </div>
             <label className="switch">
               <input
                 type="checkbox"
                 checked={data.configuration.required_approval}
                 onChange={makeToggle(
                   'required_approval',
                   updateRequiredApproval
                 )}
               />
               <span className="slider round" />
             </label>
           </div>
   
           {/* Confirmation required */}
           <div className="form-row">
             <div className="label-with-icon">
               <OverlayTrigger
                  placement="top" // Posición del tooltip
                  overlay={
                    <Tooltip id="tooltip-item-name" >
                      <div style={{ textAlign: 'left', whiteSpace: 'normal' }}>
                        El local gastronómico puede definir un periodo de tiempo para que el cliente confirme la reserva. Transcurrido ese periodo automáticamente se cancela la reserva
                      </div>
                    </Tooltip>
                  }
                >
                  <span className="ms-2 info-create-item" style={{ cursor: 'pointer' }}>
                    <FaInfoCircle size={15} color="#6c757d" /> {/* Ícono de ayuda */}
                  </span>
                </OverlayTrigger>
               <span>Requiere confirmación del cliente</span>
               
             </div>
             <label className="switch">
               <input
                 type="checkbox"
                 checked={data.configuration.confirmation_required}
                 onChange={makeToggle(
                   'confirmation_required',
                   updateConfirmationRequired
                 )}
               />
               <span className="slider round" />
             </label>
           </div>

           <div className="form-section-title">Períodos de Tiempo</div>
   
           {/* Confirmation period */}
           <div className="form-row">
             <div className="label-with-icon">
               <OverlayTrigger
                  placement="top" // Posición del tooltip
                  overlay={
                    <Tooltip id="tooltip-item-name">
                      <div style={{ textAlign: 'left', whiteSpace: 'normal' }}>
                        El local gastronómico puede definir un lapso para que el cliente confirme la reserva. Transcurrido ese periodo se cancela la reserva
                      </div>
                    </Tooltip>
                  }
                >
                  <span className="ms-2 info-create-item" style={{ cursor: 'pointer' }}>
                    <FaInfoCircle size={15} color="#6c757d" /> {/* Ícono de ayuda */}
                  </span>
                </OverlayTrigger>
               <span>Período de confirmación</span>
             </div>
             <Dropdown onSelect={(k) =>
               handlePeriod(
                 'confirmation_period',
                 k ? Number(k) : null,
                 updateConfirmationPeriod
               )
             }>
               <Dropdown.Toggle variant="secondary">
                 {data.configuration.confirmation_period != null
                   ? `${data.configuration.confirmation_period} h`
                   : 'Seleccionar…'}
               </Dropdown.Toggle>
               <Dropdown.Menu>
                 {hours.map(h => (
                   <Dropdown.Item key={h} eventKey={h.toString()}>
                     {h} hora{h > 1 ? 's' : ''}
                   </Dropdown.Item>
                 ))}
                 <Dropdown.Divider />
               </Dropdown.Menu>
             </Dropdown>
           </div>
   
           {/* Reservation period */}
           <div className="form-row">
             <div className="label-with-icon">
               <OverlayTrigger
                  placement="top" // Posición del tooltip
                  overlay={
                    <Tooltip id="tooltip-item-name">
                      <div style={{ textAlign: 'left', whiteSpace: 'normal' }}>
                        El local gastronómico puede definir con cuanta anticipación pueden reservar sus comensales. Por ejemplo, si el local selecciona 24 hrs, el cliente solo podrá hacer reservas con mínimo 24 hrs de anticipación
                      </div>
                    </Tooltip>
                  }
                >
                  <span className="ms-2 info-create-item" style={{ cursor: 'pointer' }}>
                    <FaInfoCircle size={15} color="#6c757d" /> {/* Ícono de ayuda */}
                  </span>
                </OverlayTrigger>
               <span>Período de reserva</span>
             </div>
             <Dropdown onSelect={(k) =>
               handlePeriod(
                 'reservation_period',
                 k ? Number(k) : null,
                 updateReservationPeriod
               )
             }>
               <Dropdown.Toggle variant="secondary">
                 {data.configuration.reservation_period != null
                   ? `${data.configuration.reservation_period} h`
                   : 'Seleccionar…'}
               </Dropdown.Toggle>
               <Dropdown.Menu>
                 {hours.map(h => (
                   <Dropdown.Item key={h} eventKey={h.toString()}>
                     {h} hora{h > 1 ? 's' : ''}
                   </Dropdown.Item>
                 ))}
                 <Dropdown.Divider />
               </Dropdown.Menu>
             </Dropdown>
           </div>
   
         </div>
         {/* Days & salons (deep PATCH) */}
         <ReservationDays data={data} onPatch={applyPatch} />
       </div>
     )
   }
   
   export default ReservationConfigurationPage
   